/**
 * usePapWorkScan — BLU-794.
 *
 * The PAP tab's list is a WORK QUEUE, and a work queue that stops at "400
 * scanned" and makes you click Next is not one — you cannot see how much work
 * there is. This walks the whole `tag:pap` catalogue server-side-filtered and
 * accumulates every match into ONE list, with a live scanned counter, instead
 * of cursor-paging 25 at a time.
 *
 * Deliberately the same shape as `useCoverageScan` (BLU-790) — a serial,
 * client-driven cursor walk over an already-bounded server page. The three
 * properties that cost that hook a review round apply here unchanged:
 *
 * 1. **Serial, never parallel.** One page in flight. Fanning out multiplies
 *    this org's Shopify call rate against a shared rate limit.
 * 2. **Re-entering the tab must not re-spend the Shopify budget.** Each PAGE
 *    goes through the React Query cache with an explicit `staleTime`; the app
 *    sets no default (`App.tsx:233-253`), so without it every flip back to the
 *    tab would re-walk Shopify from scratch.
 * 3. **Progressive.** Rows render as they arrive; `isComplete` says whether the
 *    list is the whole answer yet.
 *
 * The filter runs SERVER-side inside the same bounded scan the pager used
 * (`catalogue-scan.ts`), so this is a walk over already-filtered pages — it
 * never pulls the catalogue down to filter it in the browser (CLAUDE.md §10).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trpc } from '@/lib/trpc';
import type { CatalogueCopyStatus } from '../components/CatalogueCopyBanner';
import type { ImportFilter, PreviewProduct } from '../lib/types';

/**
 * Hard stop on the cursor walk. At 50 products/page that is 25,000 products —
 * well past any real PAP catalogue (Threadheads: 635) — and a runaway-cursor
 * backstop. Hitting it is reported as INCOMPLETE, never as a finished list.
 */
const MAX_PAGES = 500;

/** Server page size. Capped at 50 by `previewInputSchema`. */
const PAGE_SIZE = 50;

/** How long a fetched page stays reusable — mirrors `useCoverageScan`. */
const PAGE_STALE_MS = 5 * 60 * 1000;

export interface PapWorkScanState {
  /** Every match found so far, accumulated across pages. Renders progressively. */
  products: PreviewProduct[];
  /** Products EXAMINED so far (before the import predicate) — the counter that counts up. */
  scannedProducts: number;
  /** True once Shopify reports no further pages: `products` is then the COMPLETE filtered set. */
  isComplete: boolean;
  /** True while a page request is in flight. */
  isScanning: boolean;
  /**
   * The walk stopped without draining the catalogue and without an error —
   * a server-side scan budget dead-end, or `MAX_PAGES`. The list is partial and
   * must not be presented as the whole queue.
   */
  stoppedEarly: boolean;
  /** Set when a page request failed; the walk stops and keeps what it accumulated. */
  error: string | null;
  /** True when the server's imported-sku lookup degraded (filter was ignored). */
  importStatusUnavailable: boolean;
  papClashStatusUnavailable: boolean;
  /** BLU-1528 R3: the catalogue copy's status from the latest page, for the banner. */
  catalogueCopy: CatalogueCopyStatus | undefined;
  applyWorkStateUpdates: (updates: readonly PapWorkStateUpdate[]) => void;
  /** Discard the list and walk again from page 1, re-hitting Shopify. */
  rescan: () => void;
}

export interface PapWorkStateUpdate {
  productId: string;
  importedVariantCount: number;
  totalVariantCount: number;
  actionableVariantCount: number;
  fullyImported: boolean;
  papSkuClash: boolean;
}

export interface PapWorkScanInput {
  search: string | undefined;
  importFilter: ImportFilter;
}

/**
 * @param enabled Gate the walk — pass false whenever the PAP view isn't on screen.
 */
export function usePapWorkScan(enabled: boolean, input: PapWorkScanInput): PapWorkScanState {
  const utils = trpc.useUtils();

  const [products, setProducts] = useState<PreviewProduct[]>([]);
  const [scannedProducts, setScannedProducts] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [stoppedEarly, setStoppedEarly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importStatusUnavailable, setImportStatusUnavailable] = useState(false);
  const [papClashStatusUnavailable, setPapClashStatusUnavailable] = useState(false);
  const [catalogueCopy, setCatalogueCopy] = useState<CatalogueCopyStatus | undefined>(undefined);
  const [generation, setGeneration] = useState(0);
  // The progressive walk and mutation callbacks both write this accumulator.
  // Keeping it outside the walk closure prevents a later page from replaying a
  // stale pre-mutation snapshot over a successfully imported/resolved row.
  const productsRef = useRef<PreviewProduct[]>([]);

  // Identity of the walk. Search and the import filter both change WHICH
  // products the walk is about, so a late page from the previous identity must
  // never append to the new list.
  const scanKey = `${input.search ?? ''}::${input.importFilter}::${generation}`;
  const activeKeyRef = useRef(scanKey);
  activeKeyRef.current = scanKey;

  const rescan = useCallback(() => {
    // Bumping the generation alone would re-walk still-fresh cached pages and
    // return the identical list — a Refresh that does nothing.
    void utils.productSync.getProductSyncPreview.invalidate();
    setGeneration((g) => g + 1);
  }, [utils]);

  const applyWorkStateUpdates = useCallback((updates: readonly PapWorkStateUpdate[]) => {
    const byId = new Map(updates.map((update) => [update.productId, update]));
    const next = productsRef.current.flatMap((product) => {
      const update = byId.get(product.id);
      if (!update) return [product];
      const matches = input.importFilter === 'all'
        || (input.importFilter === 'imported' ? update.fullyImported : !update.fullyImported);
      if (!matches) return [];
      return [{
        ...product,
        syncedVariantCount: update.importedVariantCount,
        variantCount: update.totalVariantCount,
        papActionableVariantCount: update.actionableVariantCount,
        papSkuClash: update.papSkuClash,
      }];
    });
    productsRef.current = next;
    setProducts(next);
  }, [input.importFilter]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const myKey = scanKey;

    const walk = async () => {
      let cursor: string | undefined = undefined;
      let pages = 0;
      let scanned = 0;
      setIsScanning(true);
      setError(null);
      setIsComplete(false);
      setStoppedEarly(false);
      productsRef.current = [];
      setProducts([]);
      setScannedProducts(0);
      setImportStatusUnavailable(false);
      setPapClashStatusUnavailable(false);

      try {
        for (;;) {
          const page = await utils.productSync.getProductSyncPreview.fetch(
            {
              pageSize: PAGE_SIZE,
              cursor,
              search: input.search,
              productionFilter: 'pap',
              importFilter: input.importFilter,
              // PAP forces the three POD dimensions to 'all' server-side; send
              // that explicitly so the cache key can't fork per stale POD combo.
              statusFilter: 'all',
              syncFilter: 'all',
              mappingFilter: 'all',
            },
            { staleTime: PAGE_STALE_MS },
          );

          // Bail BEFORE mutating state if this walk has been superseded
          // (unmount, rescan, filter/search change).
          if (cancelled || activeKeyRef.current !== myKey) return;

          // BLU-1528 R3: the server could not continue this walk's catalogue
          // copy cursor and sent page 1, so the list starts over.
          if (page.cursorReset) {
            productsRef.current = [];
            scanned = 0;
            setImportStatusUnavailable(false);
            setPapClashStatusUnavailable(false);
          }
          productsRef.current.push(...page.products);
          scanned += page.scan.scannedProducts;
          pages += 1;

          setProducts([...productsRef.current]);
          setScannedProducts(scanned);
          if (page.importStatusUnavailable) setImportStatusUnavailable(true);
          if (page.papClashStatusUnavailable) setPapClashStatusUnavailable(true);
          setCatalogueCopy(page.catalogueCopy);

          const hitPageCap = pages >= MAX_PAGES;
          if (!page.hasNextPage || !page.endCursor || hitPageCap) {
            // `hasNextPage` is false in two very different situations:
            //  - the catalogue is genuinely drained  → COMPLETE
            //  - the server's per-request budget tripped before it could
            //    evaluate anything (`preview.handler.ts`'s dead-end guard
            //    forces hasNextPage false to stop a cursor that never moves)
            //    → PARTIAL, and saying "complete" there would under-report the
            //    queue. `scan.capped` is what separates them.
            const drained = !page.hasNextPage && !page.scan.capped && !hitPageCap;
            setIsComplete(drained);
            setStoppedEarly(!drained);
            return;
          }
          cursor = page.endCursor;
        }
      } catch (e) {
        if (cancelled || activeKeyRef.current !== myKey) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled && activeKeyRef.current === myKey) setIsScanning(false);
      }
    };

    void walk();
    return () => {
      cancelled = true;
    };
    // `utils` is a stable tRPC proxy; including it would re-run the walk on
    // every render. `input` is spread into `scanKey`, which is the real dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scanKey]);

  // Memoised: a bare object literal changes identity every render, and consumers
  // put this object in useCallback deps — which then defeats `memo(ProductSyncRow)`
  // for the whole (unvirtualized, up to 624-row) list. During a bulk run the page
  // sets state several times per product.
  return useMemo(() => ({
    products,
    scannedProducts,
    isComplete,
    isScanning,
    stoppedEarly,
    error,
    importStatusUnavailable,
    papClashStatusUnavailable,
    catalogueCopy,
    applyWorkStateUpdates,
    rescan,
  }), [
    products,
    scannedProducts,
    isComplete,
    isScanning,
    stoppedEarly,
    error,
    importStatusUnavailable,
    papClashStatusUnavailable,
    catalogueCopy,
    applyWorkStateUpdates,
    rescan,
  ]);
}
